import React from "react";
import { Button, Input, InputNumber, Popconfirm, Select, Switch, Table, Tag, Tooltip } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import {
  CONFIG_VALUE_TYPES,
  ConfigValueType,
  defaultValueForType,
  IConfigEntry,
  nextRowId,
} from "../configUtils";

interface IConfigEntriesEditorProps {
  entries: IConfigEntry[];
  onChange: (entries: IConfigEntry[]) => void;
  disabled?: boolean;
}

const TYPE_COLORS: Record<ConfigValueType, string> = {
  string: "blue",
  integer: "purple",
  double: "geekblue",
  boolean: "green",
  list: "orange",
  map: "magenta",
};

/**
 * Type-aware key/value editor for a config object. Supports adding, removing
 * and updating entries; the value editor changes with the entry's type.
 */
const ConfigEntriesEditor: React.FC<IConfigEntriesEditorProps> = ({
  entries,
  onChange,
  disabled = false,
}) => {
  const updateEntry = (rowId: string, patch: Partial<IConfigEntry>) => {
    onChange(entries.map((entry) => (entry.rowId === rowId ? { ...entry, ...patch } : entry)));
  };

  const removeEntry = (rowId: string) => {
    onChange(entries.filter((entry) => entry.rowId !== rowId));
  };

  const addEntry = () => {
    onChange([
      ...entries,
      { rowId: nextRowId(), key: "", type: "string", value: defaultValueForType("string") },
    ]);
  };

  const renderValueEditor = (entry: IConfigEntry) => {
    switch (entry.type) {
      case "boolean":
        return (
          <Switch
            checked={Boolean(entry.value)}
            disabled={disabled}
            checkedChildren="true"
            unCheckedChildren="false"
            onChange={(checked) => updateEntry(entry.rowId, { value: checked })}
          />
        );
      case "integer":
      case "double":
        return (
          <InputNumber
            className="w-full"
            value={entry.value as number | null}
            disabled={disabled}
            precision={entry.type === "integer" ? 0 : undefined}
            placeholder="null"
            onChange={(value) => updateEntry(entry.rowId, { value: value ?? null })}
          />
        );
      case "list":
        return (
          <Select
            mode="tags"
            className="w-full"
            value={(entry.value as string[]) ?? []}
            disabled={disabled}
            placeholder="Type a value and press Enter"
            tokenSeparators={[","]}
            onChange={(value: string[]) => updateEntry(entry.rowId, { value })}
          />
        );
      case "map":
        return (
          <Input.TextArea
            value={(entry.value as string) ?? ""}
            disabled={disabled}
            autoSize={{ minRows: 2, maxRows: 8 }}
            placeholder='{ "key": "value" }'
            onChange={(e) => updateEntry(entry.rowId, { value: e.target.value })}
          />
        );
      default:
        return (
          <Input
            value={(entry.value as string) ?? ""}
            disabled={disabled}
            allowClear
            placeholder="Empty saves as null"
            onChange={(e) => updateEntry(entry.rowId, { value: e.target.value })}
          />
        );
    }
  };

  const columns = [
    {
      title: "Key",
      key: "key",
      width: "30%",
      render: (_: unknown, entry: IConfigEntry) => (
        <Input
          value={entry.key}
          disabled={disabled}
          placeholder="CONFIG_KEY"
          onChange={(e) => updateEntry(entry.rowId, { key: e.target.value })}
        />
      ),
    },
    {
      title: "Type",
      key: "type",
      width: 140,
      render: (_: unknown, entry: IConfigEntry) => (
        <Select
          className="w-full"
          value={entry.type}
          disabled={disabled}
          onChange={(type: ConfigValueType) =>
            // Changing the type resets the value to that type's empty default.
            updateEntry(entry.rowId, { type, value: defaultValueForType(type) })
          }
        >
          {CONFIG_VALUE_TYPES.map((type) => (
            <Select.Option key={type} value={type}>
              <Tag color={TYPE_COLORS[type]} style={{ marginRight: 0 }}>
                {type}
              </Tag>
            </Select.Option>
          ))}
        </Select>
      ),
    },
    {
      title: "Value",
      key: "value",
      render: (_: unknown, entry: IConfigEntry) => renderValueEditor(entry),
    },
    {
      title: "",
      key: "actions",
      width: 60,
      render: (_: unknown, entry: IConfigEntry) => (
        <Popconfirm
          title={`Remove "${entry.key || "this entry"}"?`}
          onConfirm={() => removeEntry(entry.rowId)}
          disabled={disabled}
        >
          <Tooltip title="Remove entry">
            <Button danger type="text" icon={<DeleteOutlined />} disabled={disabled} />
          </Tooltip>
        </Popconfirm>
      ),
    },
  ];

  return (
    <>
      <Table
        columns={columns}
        dataSource={entries}
        rowKey="rowId"
        pagination={false}
        bordered
        size="small"
        locale={{ emptyText: "No config entries. Add one below." }}
      />
      <Button
        type="dashed"
        icon={<PlusOutlined />}
        onClick={addEntry}
        disabled={disabled}
        className="w-full mt-4"
      >
        Add Config Entry
      </Button>
    </>
  );
};

export default ConfigEntriesEditor;
